import { RSet } from "../../collections";
import { associationKey, Tristate } from "../../core";
import { ParameterMultiSet, } from "../Menu";
/**
 * Factory functions for creating Availability values.
 * NOTE: available<U>() is generic to avoid `as Availability<...>` type casts at call sites
 */
export const Availability = {
    available() {
        return {
            isAvailable: Tristate.True,
            unavailabilityReasons: RSet.empty(),
            causingParameters: ParameterMultiSet.empty(),
            errors: [],
        };
    },
    unavailable(reason) {
        const reasons = RSet.singleton(reason);
        return {
            isAvailable: Tristate.False,
            unavailabilityReasons: reasons,
            causingParameters: ParameterMultiSet.empty(),
            errors: [`Unavailable[${reasons.toString()}]`],
        };
    },
    maybe(reason) {
        const reasons = RSet.singleton(reason);
        return {
            isAvailable: Tristate.Maybe,
            unavailabilityReasons: reasons,
            causingParameters: ParameterMultiSet.empty(),
            errors: [`Maybe[${reasons.toString()}]`],
        };
    },
    composite(children) {
        const states = children.map((c) => c.isAvailable);
        const isAvailable = states.some((s) => s.isDefinitelyFalse)
            ? Tristate.False
            : states.every((s) => s.isDefinitelyTrue)
                ? Tristate.True
                : Tristate.Maybe;
        const unavailabilityReasons = children.reduce((acc, c) => acc.union(c.unavailabilityReasons), RSet.empty());
        const causingParameters = children.reduce((acc, c) => acc.union(c.causingParameters), ParameterMultiSet.empty());
        // Collect errors from unavailable children (same as DebugAvailabilityVisitor.visitComposite)
        const errors = children
            .filter((c) => c.isAvailable.isDefinitelyFalse)
            .flatMap((c) => c.errors);
        return { isAvailable, unavailabilityReasons, causingParameters, errors };
    },
    dependent(reason, description, dependency) {
        // Format errors same as DebugAvailabilityVisitor.visitDependent:
        // `${reason}(${description}) -> ${childError}`
        const errors = dependency.errors.map((error) => `${associationKey(reason).toString()}(${description}) -> ${error}`);
        const unavailabilityReasons = dependency.unavailabilityReasons;
        return {
            isAvailable: dependency.isAvailable,
            unavailabilityReasons: unavailabilityReasons.isEmpty
                ? RSet.empty()
                : unavailabilityReasons.union(RSet.singleton(reason)),
            causingParameters: dependency.causingParameters,
            errors,
        };
    },
    boolean(booleanMap) {
        const entries = Object.entries(booleanMap);
        const unavailableReasons = entries
            .filter(([_, isUnavailable]) => isUnavailable)
            .map(([reason]) => reason);
        if (unavailableReasons.length === 0) {
            return Availability.available();
        }
        const reasons = RSet.fromIterable(unavailableReasons);
        return {
            isAvailable: Tristate.False,
            unavailabilityReasons: reasons,
            causingParameters: ParameterMultiSet.empty(),
            errors: [`Boolean[${reasons.toString()}]`],
        };
    },
    parametrized(params) {
        const applied = params.parametrization.unify(params.parameterSet);
        if (applied.hasPossibleValues && applied.values.every((a) => a)) {
            return Availability.available();
        }
        const reasons = RSet.singleton(params.unavailabilityReason);
        if (applied.isValuePossible(true)) {
            return {
                isAvailable: Tristate.Maybe,
                unavailabilityReasons: reasons,
                causingParameters: ParameterMultiSet.empty(),
                errors: [`Maybe[${reasons.toString()}]`],
            };
        }
        const causingParameters = applied.hasPossibleValues
            ? applied.matchingParameters
            : applied.incompatibleParameters;
        return {
            isAvailable: Tristate.False,
            unavailabilityReasons: reasons,
            causingParameters,
            // Same format as DebugAvailabilityVisitor.visitParametrized
            errors: [`Parametrized[${causingParameters.toString()}]`],
        };
    },
    schedule(params) {
        const { fulfillmentTime } = params;
        if (fulfillmentTime === null) {
            return Availability.maybe("Schedule");
        }
        const isAvailable = params.scheduleAvailabilityService.isAvailable(params.availabilitySchedule, fulfillmentTime);
        if (isAvailable) {
            return Availability.available();
        }
        // Same format as DebugAvailabilityVisitor.visitSchedule
        // Note: original used availability.toString() which included schedule details
        const scheduleStr = params.availabilitySchedule.toString();
        return {
            isAvailable: Tristate.False,
            unavailabilityReasons: RSet.singleton("Schedule"),
            causingParameters: ParameterMultiSet.empty(),
            errors: [`Schedule[${scheduleStr}]`],
        };
    },
    preorder(params) {
        const { preorderSettings, fulfillmentTime } = params;
        // Helper to compute debug reasons (same as PreorderSettingsAvailability.reasons)
        const computeDebugReasons = () => {
            if (fulfillmentTime === null) {
                return ["DateNotSelected"];
            }
            if (!preorderSettings.canOrderAt(fulfillmentTime)) {
                return ["FulfillmentTimeNotInRange"];
            }
            return [];
        };
        if (preorderSettings.isInThePast) {
            const reasons = preorderSettings.isSameDayOnly
                ? RSet.singleton("Preorder")
                : RSet.fromIterable([
                    "Preorder",
                    "OutdatedPreorderSettings",
                ]);
            const debugReasons = computeDebugReasons();
            return {
                isAvailable: Tristate.False,
                unavailabilityReasons: reasons,
                causingParameters: ParameterMultiSet.empty(),
                // Same format as DebugAvailabilityVisitor.visitPreorder
                errors: [`Preorder(${debugReasons.join(", ")})`],
            };
        }
        if (fulfillmentTime === null) {
            // FIXME: Should be Maybe, but breaks e2e tests
            return Availability.available();
        }
        if (preorderSettings.canOrderAt(fulfillmentTime)) {
            return Availability.available();
        }
        const debugReasons = computeDebugReasons();
        return {
            isAvailable: Tristate.False,
            unavailabilityReasons: RSet.singleton("Preorder"),
            causingParameters: ParameterMultiSet.empty(),
            // Same format as DebugAvailabilityVisitor.visitPreorder
            errors: [`Preorder(${debugReasons.join(", ")})`],
        };
    },
};
